import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { loadMarketInsights } from '../api';

const ONE_HOUR_IN_MS = 60 * 60 * 1000;

/**
 * Custom hook to fetch market insights for selected commodities.
 * Uses a single POST request (the endpoint accepts an array of commodities).
 * Results are sorted: active first, then by impact (high > medium > low).
 *
 * @param {string[]} commodities - Array of commodity names
 * @returns {Object} - { insights, isLoading, isError, refetch }
 */
export const useMarketInsights = (commodities = []) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  const sortedCommodities = useMemo(
    () => [...commodities].sort().join(','),
    [commodities]
  );

  const query = useQuery({
    queryKey: ['marketInsights', sortedCommodities, language],
    queryFn: () => loadMarketInsights(commodities, language),
    enabled: commodities.length > 0,
    staleTime: ONE_HOUR_IN_MS,
    gcTime: ONE_HOUR_IN_MS,
  });

  const sortedInsights = useMemo(() => {
    const insights = query.data || [];
    const impactOrder = { high: 0, alto: 0, medium: 1, medio: 1, low: 2, bajo: 2 };

    return [...insights].sort((a, b) => {
      // Active first (by expires_at)
      const aActive = new Date(a.expires_at) > new Date();
      const bActive = new Date(b.expires_at) > new Date();
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;

      // Then by impact
      const aImpact = impactOrder[a.market_impact?.toLowerCase()] ?? 3;
      const bImpact = impactOrder[b.market_impact?.toLowerCase()] ?? 3;
      return aImpact - bImpact;
    });
  }, [query.data]);

  return {
    insights: sortedInsights,
    isLoading: query.isLoading || query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
};
